import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, Plus, Trash2, MessageSquare, ChevronDown, ChevronUp, Pencil, Download } from 'lucide-react';
import { fmtMoney as fmt } from '../../utils/money';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getPeriodo() {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const inicio = new Date(y, m, 1).toISOString().split('T')[0];
  const fin = new Date(y, m + 1, 0).toISOString().split('T')[0];
  return { inicio, fin };
}

export function AdminNomina() {
  const navigate = useNavigate();
  const periodo = getPeriodo();
  const [fechaInicio, setFechaInicio] = useState(periodo.inicio);
  const [fechaFin, setFechaFin] = useState(periodo.fin);
  const [nomina, setNomina] = useState([]);
  const [loading, setLoading] = useState(false);
  const [aclaraciones, setAclaraciones] = useState([]);
  const [expandido, setExpandido] = useState(null);
  const [modalAjuste, setModalAjuste] = useState(null); // { user_id, nombre }
  const [ajusteForm, setAjusteForm] = useState({ tipo: 'percepcion', concepto: '', monto: '' });
  const [modalSueldo, setModalSueldo] = useState(null); // { user_id, nombre, sueldo_diario }
  const [nuevoSueldo, setNuevoSueldo] = useState('');
  const [modalAclaracion, setModalAclaracion] = useState(null);
  const [respuestaTexto, setRespuestaTexto] = useState('');
  const [editandoDeduccion, setEditandoDeduccion] = useState(null);
  const [montoEditado, setMontoEditado] = useState('');

  const loadNomina = useCallback(async () => {
    setLoading(true);
    try {
      const [nominaRes, aclarRes] = await Promise.all([
        axios.get(`${API}/admin/nomina?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`),
        axios.get(`${API}/admin/nomina/aclaraciones`),
      ]);
      setNomina(nominaRes.data.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')));
      setAclaraciones(aclarRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [fechaInicio, fechaFin]);

  useEffect(() => { loadNomina(); }, [loadNomina]);

  const handleAgregarAjuste = async () => {
    if (!ajusteForm.concepto || !ajusteForm.monto) return;
    await axios.post(`${API}/admin/nomina/ajuste`, {
      user_id: modalAjuste.user_id,
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin,
      tipo: ajusteForm.tipo,
      concepto: ajusteForm.concepto,
      monto: parseFloat(ajusteForm.monto),
    });
    setModalAjuste(null);
    setAjusteForm({ tipo: 'percepcion', concepto: '', monto: '' });
    loadNomina();
  };

  const handleEliminarAjuste = async (ajusteId) => {
    await axios.delete(`${API}/admin/nomina/ajuste/${ajusteId}`);
    loadNomina();
  };

  const handleGuardarSueldo = async () => {
    await axios.patch(`${API}/users/${modalSueldo.user_id}/sueldo?sueldo_diario=${parseFloat(nuevoSueldo)}`);
    setModalSueldo(null);
    loadNomina();
  };

  const handleAtenderAclaracion = async (id) => {
    await axios.put(`${API}/admin/nomina/aclaracion/${id}/atender`, { respuesta: respuestaTexto });
    setModalAclaracion(null);
    setRespuestaTexto('');
    loadNomina();
  };

  const handleOverrideDeduccion = async (emp, corteId, concepto) => {
    const monto = parseFloat(montoEditado);
    if (isNaN(monto) || monto < 0) return;
    await axios.post(`${API}/admin/nomina/ajuste`, {
      user_id: emp.user_id,
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin,
      tipo: 'deduccion',
      concepto,
      monto,
      corte_id: corteId,
    });
    setEditandoDeduccion(null);
    setMontoEditado('');
    loadNomina();
  };

  const pendientesCount = aclaraciones.length;

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col">
      {/* Header */}
      <div className="bg-white px-4 py-3 border-b border-zinc-200">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-zinc-900">Nómina</h1>
            <p className="text-sm text-zinc-500">Cálculo de percepciones y deducciones</p>
          </div>
          {pendientesCount > 0 && (
            <button
              onClick={() => setModalAclaracion(aclaraciones[0])}
              className="flex items-center gap-1 px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-sm font-medium"
            >
              <MessageSquare className="w-4 h-4" />
              {pendientesCount} aclaración{pendientesCount > 1 ? 'es' : ''}
            </button>
          )}
        </div>
      </div>

      {/* Periodo */}
      <div className="px-4 py-3 bg-white border-b border-zinc-200">
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="text-xs font-medium text-zinc-500 mb-1 block">Desde</label>
            <input type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)}
              className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm" />
          </div>
          <div className="flex-1">
            <label className="text-xs font-medium text-zinc-500 mb-1 block">Hasta</label>
            <input type="date" value={fechaFin} onChange={e => setFechaFin(e.target.value)}
              className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm" />
          </div>
          <button onClick={loadNomina} className="h-10 px-4 bg-zinc-900 text-white rounded-lg text-sm font-medium">
            Calcular
          </button>
          <a
            href={`${API}/admin/nomina/excel?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`}
            target="_blank"
            rel="noreferrer"
            className="h-10 px-3 border border-zinc-300 rounded-lg flex items-center justify-center text-zinc-700 hover:bg-zinc-50"
            title="Exportar Excel"
          >
            <Download className="w-4 h-4" />
          </a>
        </div>
      </div>

      <div className="flex-1 px-4 py-4 space-y-3">
        {loading ? (
          <div className="flex justify-center py-12">
            <span className="w-8 h-8 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
          </div>
        ) : nomina.length === 0 ? (
          <p className="text-center text-zinc-400 py-12">No hay empleados registrados</p>
        ) : (
          nomina.map(emp => {
            const isOpen = expandido === emp.user_id;
            return (
              <div key={emp.user_id} className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
                {/* Cabecera empleado */}
                <div
                  className="px-4 py-3 flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandido(isOpen ? null : emp.user_id)}
                >
                  <div>
                    <p className="font-bold text-zinc-900">{emp.nombre}</p>
                    <p className="text-xs text-zinc-500 capitalize">{emp.rol} · {emp.dias_trabajados} día{emp.dias_trabajados !== 1 ? 's' : ''} trabajado{emp.dias_trabajados !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-lg font-bold ${emp.total_neto >= 0 ? 'text-zinc-900' : 'text-red-600'}`}>
                      {fmt(emp.total_neto)}
                    </span>
                    {isOpen ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
                  </div>
                </div>

                {isOpen && (
                  <div className="border-t border-zinc-100 px-4 py-3 space-y-4">
                    {/* Sueldo diario */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-zinc-700">Sueldo diario</p>
                        <p className="text-xs text-zinc-400">{emp.dias_trabajados} días × {fmt(emp.sueldo_diario)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-green-600 font-bold">{fmt(emp.percepcion_base)}</span>
                        <button
                          onClick={() => { setModalSueldo({ user_id: emp.user_id, nombre: emp.nombre, sueldo_diario: emp.sueldo_diario }); setNuevoSueldo(String(emp.sueldo_diario)); }}
                          className="p-1 text-zinc-400 hover:text-zinc-700"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Deducciones por corte */}
                    {emp.deducciones_corte.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-zinc-500 mb-2">DEDUCCIONES POR CORTE</p>
                        {emp.deducciones_corte.map(d => (
                          <div key={d.corte_id} className="flex items-center justify-between py-1.5">
                            <div>
                              <p className="text-sm text-zinc-700">{d.concepto}</p>
                              {d.modificado && <span className="text-xs text-amber-600">Monto modificado</span>}
                            </div>
                            <div className="flex items-center gap-2">
                              {editandoDeduccion === d.corte_id ? (
                                <>
                                  <input
                                    type="number" min="0" value={montoEditado}
                                    onChange={e => setMontoEditado(e.target.value)}
                                    className="w-24 h-8 px-2 border border-zinc-300 rounded text-sm text-right"
                                    autoFocus
                                  />
                                  <button onClick={() => handleOverrideDeduccion(emp, d.corte_id, d.concepto)} className="text-xs px-2 py-1 bg-zinc-900 text-white rounded">OK</button>
                                  <button onClick={() => setEditandoDeduccion(null)} className="text-xs px-2 py-1 border border-zinc-300 rounded">✕</button>
                                </>
                              ) : (
                                <>
                                  <span className="text-red-600 font-bold">-{fmt(d.monto)}</span>
                                  <button onClick={() => { setEditandoDeduccion(d.corte_id); setMontoEditado(String(d.monto)); }} className="p-1 text-zinc-400 hover:text-zinc-700">
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Ajustes manuales */}
                    {emp.ajustes.filter(a => !a.corte_id).length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-zinc-500 mb-2">AJUSTES MANUALES</p>
                        {emp.ajustes.filter(a => !a.corte_id).map(a => (
                          <div key={a.id} className="flex items-center justify-between py-1.5">
                            <p className="text-sm text-zinc-700">{a.concepto}</p>
                            <div className="flex items-center gap-2">
                              <span className={`font-bold ${a.tipo === 'percepcion' ? 'text-green-600' : 'text-red-600'}`}>
                                {a.tipo === 'percepcion' ? '+' : '-'}{fmt(a.monto)}
                              </span>
                              <button onClick={() => handleEliminarAjuste(a.id)} className="p-1 text-red-400 hover:text-red-600">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Total */}
                    <div className="flex justify-between items-center pt-2 border-t border-zinc-100">
                      <span className="font-bold text-zinc-900">Total neto</span>
                      <span className={`text-xl font-bold ${emp.total_neto >= 0 ? 'text-zinc-900' : 'text-red-600'}`}>{fmt(emp.total_neto)}</span>
                    </div>

                    {/* Botón agregar ajuste */}
                    <button
                      onClick={() => setModalAjuste({ user_id: emp.user_id, nombre: emp.nombre })}
                      className="w-full h-10 border border-zinc-300 rounded-lg text-sm font-medium text-zinc-700 flex items-center justify-center gap-2 hover:bg-zinc-50"
                    >
                      <Plus className="w-4 h-4" /> Agregar ajuste manual
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Modal: Agregar ajuste */}
      {modalAjuste && (
        <div className="fixed inset-0 z-50 flex items-end bg-black/40">
          <div className="w-full bg-white rounded-t-2xl p-5 space-y-4">
            <h2 className="font-bold text-zinc-900">Ajuste para {modalAjuste.nombre}</h2>
            <div className="flex gap-2">
              {['percepcion', 'deduccion'].map(t => (
                <button key={t} onClick={() => setAjusteForm(f => ({ ...f, tipo: t }))}
                  className={`flex-1 h-10 rounded-lg text-sm font-medium border ${ajusteForm.tipo === t ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-300 text-zinc-700'}`}>
                  {t === 'percepcion' ? 'Percepción' : 'Deducción'}
                </button>
              ))}
            </div>
            <input placeholder="Concepto" value={ajusteForm.concepto}
              onChange={e => setAjusteForm(f => ({ ...f, concepto: e.target.value }))}
              className="w-full h-11 px-3 border border-zinc-300 rounded-lg text-sm" />
            <input placeholder="Monto" type="number" min="0" value={ajusteForm.monto}
              onChange={e => setAjusteForm(f => ({ ...f, monto: e.target.value }))}
              className="w-full h-11 px-3 border border-zinc-300 rounded-lg text-sm" />
            <div className="flex gap-2">
              <button onClick={() => setModalAjuste(null)} className="flex-1 h-11 border border-zinc-300 rounded-xl text-sm">Cancelar</button>
              <button onClick={handleAgregarAjuste} className="flex-1 h-11 bg-zinc-900 text-white rounded-xl text-sm font-medium">Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Editar sueldo */}
      {modalSueldo && (
        <div className="fixed inset-0 z-50 flex items-end bg-black/40">
          <div className="w-full bg-white rounded-t-2xl p-5 space-y-4">
            <h2 className="font-bold text-zinc-900">Sueldo diario — {modalSueldo.nombre}</h2>
            <input placeholder="Sueldo diario $" type="number" min="0" value={nuevoSueldo}
              onChange={e => setNuevoSueldo(e.target.value)}
              className="w-full h-11 px-3 border border-zinc-300 rounded-lg text-sm" />
            <div className="flex gap-2">
              <button onClick={() => setModalSueldo(null)} className="flex-1 h-11 border border-zinc-300 rounded-xl text-sm">Cancelar</button>
              <button onClick={handleGuardarSueldo} className="flex-1 h-11 bg-zinc-900 text-white rounded-xl text-sm font-medium">Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Aclaración pendiente */}
      {modalAclaracion && (
        <div className="fixed inset-0 z-50 flex items-end bg-black/40">
          <div className="w-full bg-white rounded-t-2xl p-5 space-y-4">
            <h2 className="font-bold text-zinc-900">Aclaración de {modalAclaracion.user_name}</h2>
            <p className="text-sm text-zinc-600 bg-zinc-50 rounded-lg p-3">{modalAclaracion.texto}</p>
            <p className="text-xs text-zinc-400">Periodo: {modalAclaracion.fecha_inicio} al {modalAclaracion.fecha_fin}</p>
            <textarea
              placeholder="Respuesta al empleado (opcional)"
              value={respuestaTexto}
              onChange={e => setRespuestaTexto(e.target.value)}
              className="w-full h-24 px-3 py-2 border border-zinc-300 rounded-lg text-sm resize-none"
            />
            <div className="flex gap-2">
              {aclaraciones.length > 1 && (
                <button onClick={() => { const next = aclaraciones.find(a => a.id !== modalAclaracion.id); setModalAclaracion(next || null); setRespuestaTexto(''); }}
                  className="flex-1 h-11 border border-zinc-300 rounded-xl text-sm">
                  Siguiente ({aclaraciones.length - 1})
                </button>
              )}
              <button onClick={() => { setModalAclaracion(null); setRespuestaTexto(''); }} className="flex-1 h-11 border border-zinc-300 rounded-xl text-sm">Cerrar</button>
              <button onClick={() => handleAtenderAclaracion(modalAclaracion.id)} className="flex-1 h-11 bg-zinc-900 text-white rounded-xl text-sm font-medium">Marcar atendida</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
